import React, { useState, useEffect } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Box,
    IconButton,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axiosInstance from '../utills/axiosInstance';
import moment from 'moment';
import { usePermissions } from '../utills/usePermissions';

const OdDashboard = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openActionDialog, setOpenActionDialog] = useState(false);
    const [openBillDialog, setOpenBillDialog] = useState(false);
    const [selectedOD, setSelectedOD] = useState(null);
    const [actionType, setActionType] = useState('');
    const [comments, setComments] = useState('');

    const { canApprove, canReject } = usePermissions();

    useEffect(() => {
        fetchPendingODs();
    }, []);

    const fetchPendingODs = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axiosInstance.get('/api/od/allpending');
            setRequests(res.data.data || []);
        } catch (err) {
            setError('Failed to load pending OD requests');
        } finally {
            setLoading(false);
        }
    };

    const handleAction = (od, type) => {
        setSelectedOD(od);
        setActionType(type);
        setComments('');
        setOpenActionDialog(true);
    };

    const handleViewBills = (od) => {
        setSelectedOD(od);
        setOpenBillDialog(true);
    };

    const confirmAction = async () => {
        if (!selectedOD) return;

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const endpoint = actionType === 'approve'
                ? `/api/od/${selectedOD._id}/approve`
                : `/api/od/${selectedOD._id}/reject`;
            await axiosInstance.post(endpoint, { comments });

            setSuccess(`OD request ${actionType === 'approve' ? 'approved' : 'rejected'} successfully`);
            setTimeout(() => setSuccess(''), 4000);
            fetchPendingODs();
            setOpenActionDialog(false);
        } catch (err) {
            setError(err.response?.data?.msg || 'Action failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
            <Typography variant="h5" textAlign="center" mb={4} fontWeight="bold">
                Pending On Duty (OD) Requests
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
                    <CircularProgress />
                </Box>
            ) : requests.length === 0 ? (
                <Alert severity="info">No pending OD requests</Alert>
            ) : (
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#1976d2' }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Employee</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Reason</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Allowances</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {requests.map((od) => (
                                <TableRow key={od._id} hover>
                                    <TableCell>
                                        <strong>{od.employee.firstName} {od.employee.lastName}</strong>
                                        <br />
                                        <Typography variant="body2" color="text.secondary">
                                            {od.employee.empId} - {od.employee.email}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {moment(od.date).format('ddd, MMM DD, YYYY')}
                                    </TableCell>
                                    <TableCell>
                                        {od.reason || <em>No reason provided</em>}
                                    </TableCell>
                                    <TableCell>
                                        {od.allowances?.enabled ? (
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {od.allowances.food?.amount > 0 && (
                                                    <Chip label={`Food: ₹${od.allowances.food.amount}`} size="small" color="success" />
                                                )}
                                                {od.allowances.fuel?.amount > 0 && (
                                                    <Chip label={`Fuel: ₹${od.allowances.fuel.amount}`} size="small" color="primary" />
                                                )}
                                                {(od.allowances.food?.billUrl || od.allowances.fuel?.billUrl) && (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        startIcon={<VisibilityIcon />}
                                                        onClick={() => handleViewBills(od)}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        View Bills
                                                    </Button>
                                                )}
                                            </Box>
                                        ) : (
                                            <Chip label="No Allowance" size="small" color="default" />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {
                                            canApprove('od') && (
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    size="small"
                                                    startIcon={<CheckIcon />}
                                                    onClick={() => handleAction(od, 'approve')}
                                                    sx={{ mr: 1 }}
                                                >
                                                    Approve
                                                </Button>
                                            )
                                        }
                                        {
                                            canReject('od') && (
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    size="small"
                                                    startIcon={<CloseIcon />}
                                                    onClick={() => handleAction(od, 'reject')}
                                                >
                                                    Reject
                                                </Button>
                                            )
                                        }
                                        {
                                            !canApprove('od') && !canReject('od') && (
                                                <Typography>No permissions for approve or reject</Typography>
                                            )
                                        }
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            <Dialog open={openActionDialog} onClose={() => setOpenActionDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {actionType === 'approve' ? 'Approve' : 'Reject'} OD Request
                    <IconButton onClick={() => setOpenActionDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {selectedOD && (
                        <Box sx={{ my: 2 }}>
                            <Typography><strong>Employee:</strong> {selectedOD.employee.firstName} {selectedOD.employee.lastName}</Typography>
                            <Typography><strong>Date:</strong> {moment(selectedOD.date).format('MMMM DD, YYYY')}</Typography>
                            <Typography><strong>Reason:</strong> {selectedOD.reason || 'N/A'}</Typography>
                        </Box>
                    )}
                    <TextField
                        label="Comments (Optional)"
                        multiline
                        rows={3}
                        fullWidth
                        value={comments}
                        onChange={(e) => setComments(e.target.value)}
                        placeholder="Add any notes for the employee..."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenActionDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color={actionType === 'approve' ? 'success' : 'error'}
                        onClick={confirmAction}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={20} /> : actionType === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openBillDialog} onClose={() => setOpenBillDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    OD Allowance Bills
                    <IconButton onClick={() => setOpenBillDialog(false)} sx={{ position: 'absolute', right: 8, top: 8 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedOD && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, my: 2 }}>
                            {selectedOD.allowances?.food?.billUrl ? (
                                <Box textAlign="center">
                                    <Typography variant="h6" gutterBottom>Food Bill</Typography>
                                    <img
                                        src={selectedOD.allowances.food.billUrl}
                                        alt="Food Bill"
                                        style={{ maxWidth: '100%', maxHeight: '600px', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                                    />
                                </Box>
                            ) : (
                                <Typography color="text.secondary" textAlign="center">No food bill uploaded</Typography>
                            )}
                            {selectedOD.allowances?.fuel?.billUrl ? (
                                <Box textAlign="center">
                                    <Typography variant="h6" gutterBottom>Fuel Bill</Typography>
                                    <img
                                        src={selectedOD.allowances.fuel.billUrl}
                                        alt="Fuel Bill"
                                        style={{ maxWidth: '100%', maxHeight: '600px', borderRadius: '8px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)' }}
                                    />
                                </Box>
                            ) : (
                                <Typography color="text.secondary" textAlign="center">No fuel bill uploaded</Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenBillDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default OdDashboard;
